import { ApplicationConfig, provideZoneChangeDetection, importProvidersFrom, provideAppInitializer, inject, isDevMode, ErrorHandler } from '@angular/core';
import { AppTokenStorageService } from './token-storage.service';
import { environment } from '../environments/environment';
import { forkJoin, Observable, tap } from 'rxjs';
import { HttpClient, provideHttpClient, withFetch, HTTP_INTERCEPTORS, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { provideRouter, Router, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { routes } from './app.routes';
import { SessionService } from './session/session.service';
import { SP_API_URL, TOKEN_STORAGE, AuthInterceptor, ApiErrorInterceptor } from 'sp-client';
import { SP_IMAGE_URL } from './core/image-url.token';
import { TenantService } from './services/tenant.service';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import * as Sentry from "@sentry/angular";
import { FullscreenOverlayContainer } from '@angular/cdk/overlay';
import { OverlayContainer } from '@angular/cdk/overlay';

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}

export function initializeSession(): Observable<unknown> {
  const sessionService = inject(SessionService);
  const tenantService = inject(TenantService);
  return sessionService.restore().pipe(
    tap(() => {
      tenantService.clientsResource.value();
    })
  );
}

export function initializeTranslate(): Observable<unknown> {
  const translate = inject(TranslateService);
  return translate.use('de');
}

export function initializeApp(): Observable<unknown> {
  return forkJoin([
    initializeSession(),
    initializeTranslate()
  ]);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),

    provideAppInitializer(initializeApp),

    { provide: TOKEN_STORAGE, useExisting: AppTokenStorageService },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiErrorInterceptor, multi: true },

    provideHttpClient(withFetch(), withInterceptorsFromDi()),

    { provide: SP_API_URL, useValue: environment.apiUrl ?? `https://api-${environment.tenantSlug}.simply-pos.app/api/`, },
    { provide: SP_IMAGE_URL, useValue: environment.imageUrl ?? `https://api-${environment.tenantSlug}.simply-pos.app/` },
    provideAnimationsAsync(),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      })
    ),
    provideLuxonDateAdapter(),
    provideCharts(withDefaultRegisterables()), 
    TranslateService, provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
  ]
};
